import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientCatalogItem, ClientCatalogService } from 'src/app/services/client-catalog.service';
import { environment } from 'src/environments/environment';
import { DeliveryService, DeliveryOutputDto } from 'src/generated_client';

export interface FixedPointShiftSessionDetail {
  id: number;
  cdDelivery: number;
  startTime: string;
  endTime: string | null;
  deliveryName: string;
  deliveryPhone: string;
  hours: string | null;
  client: string | null;
  cdClient: number | null;
}

type ShiftSessionDetailApiResponse =
  | FixedPointShiftSessionDetail
  | { data?: FixedPointShiftSessionDetail };

@Component({
  selector: 'app-edit-shift-session',
  templateUrl: './edit-shift-session.component.html',
  styleUrls: ['./edit-shift-session.component.css'],
})
export class EditShiftSessionComponent implements OnInit, OnDestroy {
  private readonly argentinaTimeZone = 'America/Argentina/Buenos_Aires';

  sessionId: number | null = null;
  session: FixedPointShiftSessionDetail | null = null;
  cadetes: DeliveryOutputDto[] = [];
  isLoading = false;
  isEditMode = false;

  clients: ClientCatalogItem[] = [];
  filteredClients: ClientCatalogItem[] = [];
  selectedClientId: number | null = null;
  private destroy$ = new Subject<void>();

  private defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private deliveryService: DeliveryService,
    private clientCatalog: ClientCatalogService
  ) {}

  ngOnInit(): void {
    this.loadCadetes();
    this.loadClients();
    
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.sessionId = +params['id'];
        this.isEditMode = true;
        this.loadShiftSession();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  calculateTotalHours(): void {
    if (this.session && this.session.startTime && this.session.endTime) {
      const start = new Date(this.session.startTime);
      const end = new Date(this.session.endTime);
      const diffMs = end.getTime() - start.getTime();
      this.session.hours = this.millisecondsToWorkedHour(diffMs);
    }
  }

  loadShiftSession(): void {
    if (!this.sessionId) return;

    this.isLoading = true;
    const url = `${environment.apiBaseUrl}/api/WorkShiftSession/GetById/${this.sessionId}`;
    
    this.http.get<ShiftSessionDetailApiResponse>(url).subscribe({
      next: (response) => {
        const session = (response as { data?: FixedPointShiftSessionDetail })?.data
          ? (response as { data?: FixedPointShiftSessionDetail }).data!
          : (response as FixedPointShiftSessionDetail);

        this.session = {
          ...session,
          deliveryName: (session as any).deliveryName || (session as any).dsName || '',
          deliveryPhone: (session as any).deliveryPhone || (session as any).dsPhone || '',
          client: (session as any).client || (session as any).clientDescription || null,
          cdClient: (session as any).cdClient ?? null,
          hours: this.normalizeWorkedHour(
            (session as any).WorkedHours ??
            (session as any).workedHours ??
            (session as any).WorkedHour ??
            (session as any).workedHour ??
            (session as any).hours ??
            (session as any).totalHours ??
            null
          ),
        };

        this.fillCadeteDataFromList();

        if (this.session.cdClient) {
          this.selectedClientId = this.session.cdClient;
        }

        if (this.session.endTime && !this.session.hours) {
          this.calculateTotalHours();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading shift session', err);
        this.snackBar.open('Error al cargar el turno', 'Cerrar', {
          duration: 3000,
        });
        this.isLoading = false;
      },
    });
  }

  loadCadetes(): void {
    this.deliveryService.apiDeliveryGetAllGet().subscribe({
      next: (response: unknown) => {
        // GET /api/Delivery/GetAll devuelve PagedResult {data:[...]} desde el PR 3209;
        // el cliente generado todavía declara Array (contrato viejo), por eso se desempaqueta.
        this.cadetes = (response as { data?: DeliveryOutputDto[] })?.data ?? [];
        this.fillCadeteDataFromList();
      },
      error: (err: any) => {
        console.error('Error al cargar cadetes', err);
      }
    });
  }

  loadClients(): void {
    this.clientCatalog.getAll(['Punto Fijo']).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: clients => {
        this.clients = clients;
        this.filteredClients = [...this.clients];
      },
      error: err => {
        console.error('Error loading clients', err);
        this.clients = [];
        this.filteredClients = [];
        this.snackBar.open('No se pudieron cargar los clientes de Punto Fijo.', 'Cerrar', { duration: 4000 });
      }
    });
  }

  filterClients(event: any): void {
    const search = (event.target.value || '').toLowerCase();
    if (!search) {
      this.filteredClients = [...this.clients];
      return;
    }
    this.filteredClients = this.clients.filter(c =>
      c.dsNombreFantasia?.toLowerCase().includes(search) ||
      c.dsRazonSocial?.toLowerCase().includes(search) ||
      c.dsCUIT?.includes(search)
    );
  }

  onClientSelected(client: ClientCatalogItem): void {
    this.selectedClientId = client.cdClient;
    if (this.session) {
      this.session.client = client.dsNombreFantasia || client.dsRazonSocial || null;
    }
  }

  onClientInput(): void {
    const value = this.session?.client;
    const match = this.clients.find(c =>
      c.dsNombreFantasia === value || c.dsRazonSocial === value
    );
    if (!match) {
      this.selectedClientId = null;
    }
  }

  displayClientName(client: any): string {
    return client ? (client.dsNombreFantasia || client.dsRazonSocial || '') : '';
  }

  private fillCadeteDataFromList(): void {
    if (!this.session || !this.cadetes?.length) {
      return;
    }

    const selectedCadete = this.cadetes.find(cadete => cadete.cdDelivery === this.session!.cdDelivery);
    if (!selectedCadete) {
      return;
    }

    if (!this.session.deliveryName) {
      this.session.deliveryName = selectedCadete.dsName || '';
    }

    if (!this.session.deliveryPhone) {
      this.session.deliveryPhone = selectedCadete.dsPhone || '';
    }
  }

  onSave(): void {
    if (!this.session) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        message: '¿Está seguro que desea guardar los cambios?',
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.saveSession();
      }
    });
  }

  private saveSession(): void {
    if (!this.session) return;

    const workedHours = this.normalizeWorkedHour(this.formatTime(this.session.hours));
    if (this.session.hours && !workedHours) {
      this.snackBar.open('Las horas trabajadas deben tener formato hh:mm:ss', 'Cerrar', {
        duration: 3500,
      });
      return;
    }
   
    const url = `${environment.apiBaseUrl}/api/WorkShiftSession/Update`;
    const payload = {
      id: this.session.id,
      endTime: this.session.endTime,
      client: this.session.client,
      cdClient: this.selectedClientId ?? undefined,
      workedHours: workedHours || null,
    };

    this.http.put(url, payload, { headers: this.defaultHeaders }).subscribe({
      next: () => {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '350px',
          data: {
            message: 'Turno actualizado correctamente',
            confirmButtonText: 'Aceptar',
            cancelButtonText: '',
            showCancelButton: false,
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          this.router.navigate(['/delivery-records']);
        });
      },
      error: (err) => {
        console.error('Error updating shift session', err);
        this.dialog.open(ConfirmDialogComponent, {
          width: '350px',
          data: {
            message: 'Ocurrió un error al actualizar el turno',
            confirmButtonText: 'Aceptar',
            showCancelButton: false,
          },
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/delivery-records']);
  }

  onWorkedHourBlur(): void {
    if (!this.session) {
      return;
    }

    this.session.hours = this.normalizeWorkedHour(this.session.hours);
  }

  onEndShift(): void {
    if (!this.session || this.session.endTime) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        message: '¿Está seguro que desea finalizar este turno?',
        confirmButtonText: 'Finalizar',
        cancelButtonText: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.endShift();
      }
    });
  }

  private endShift(): void {
    const url = `${environment.apiBaseUrl}/api/WorkShiftSession/EndShift/${this.sessionId}`;
    
    this.http.post(url, {}, { headers: this.defaultHeaders }).subscribe({
      next: () => {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '350px',
          data: {
            message: 'Turno finalizado correctamente',
            confirmButtonText: 'Aceptar',
            cancelButtonText: '',
            showCancelButton: false,
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          this.loadShiftSession();
          if (this.session) {
            this.calculateTotalHours();
          }
        });
      },
      error: (err) => {
        console.error('Error ending shift', err);
        this.dialog.open(ConfirmDialogComponent, {
          width: '350px',
          data: {
            message: 'Ocurrió un error al finalizar el turno',
            confirmButtonText: 'Aceptar',
            showCancelButton: false,
          },
        });
      }
    });
  }

  formatUtcToArgentina(dateValue: string | null): string {
    if (!dateValue) {
      return 'En curso';
    }

    const parsedDate = this.parseUtcDate(dateValue);
    if (!parsedDate) {
      return dateValue;
    }

    return new Intl.DateTimeFormat('es-AR', {
      timeZone: this.argentinaTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsedDate);
  }

  private parseUtcDate(dateValue: string): Date | null {
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateValue);
    const utcDateValue = hasTimezone ? dateValue : `${dateValue}Z`;
    const parsedDate = new Date(utcDateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  private formatTime(time: string | null): string | null {
    if (!time) {
      return null;
    }

    const trimmedTime = time.trim();
    if (!trimmedTime) {
      return null;
    }

    return trimmedTime.length === 5 ? `${trimmedTime}:00` : trimmedTime;
  }

  private normalizeWorkedHour(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'object') {
      const parsedFromObject = this.workedHourFromBackendObject(value as Record<string, unknown>);
      if (parsedFromObject !== null) {
        return parsedFromObject;
      }
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.decimalHoursToWorkedHour(value);
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const decimalCandidate = Number(trimmed.replace(',', '.'));
    if (Number.isFinite(decimalCandidate) && /^\d+(?:[\.,]\d+)?$/.test(trimmed)) {
      return this.decimalHoursToWorkedHour(decimalCandidate);
    }

    // Accept .NET TimeSpan strings: hh:mm[:ss][.fffffff] and d.hh:mm:ss[.fffffff]
    const timeSpanMatch = /^(?:(\d+)\.)?(\d{1,4}):(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?$/.exec(trimmed);
    if (!timeSpanMatch) {
      return null;
    }

    const dayPart = Number(timeSpanMatch[1] ?? '0');
    const baseHours = Number(timeSpanMatch[2]);
    const minutes = Number(timeSpanMatch[3]);
    const seconds = Number(timeSpanMatch[4] ?? '0');
    const hours = dayPart * 24 + baseHours;

    if (minutes > 59 || seconds > 59) {
      return null;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private workedHourFromBackendObject(value: Record<string, unknown>): string | null {
    const hasValue = value['hasValue'];
    const wrappedValue = value['value'];

    if (typeof hasValue === 'boolean' && !hasValue) {
      return null;
    }

    if (wrappedValue && typeof wrappedValue === 'object') {
      const dtoValue = wrappedValue as Record<string, unknown>;

      const hours = this.toFiniteNumber(dtoValue['hours']);
      const minutes = this.toFiniteNumber(dtoValue['minutes']);
      const seconds = this.toFiniteNumber(dtoValue['seconds']);

      if (hours !== null && minutes !== null && seconds !== null) {
        return `${String(Math.max(0, Math.trunc(hours))).padStart(2, '0')}:${String(Math.max(0, Math.trunc(minutes))).padStart(2, '0')}:${String(Math.max(0, Math.trunc(seconds))).padStart(2, '0')}`;
      }

      const totalSeconds = this.toFiniteNumber(dtoValue['totalSeconds']);
      if (totalSeconds !== null) {
        return this.millisecondsToWorkedHour(totalSeconds * 1000);
      }

      const ticks = this.toFiniteNumber(dtoValue['ticks']);
      if (ticks !== null) {
        // .NET TimeSpan tick = 100ns.
        return this.millisecondsToWorkedHour(ticks / 10000);
      }
    }

    return null;
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return null;
  }

  private millisecondsToWorkedHour(milliseconds: number): string {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
      return '00:00:00';
    }

    const totalSeconds = Math.round(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private decimalHoursToWorkedHour(decimalHours: number): string {
    const safeHours = Number.isFinite(decimalHours) ? Math.max(0, decimalHours) : 0;
    const totalSeconds = Math.round(safeHours * 3600);
    return this.millisecondsToWorkedHour(totalSeconds * 1000);
  }
}
